/**
 * Calendar-invite preferences shared by every place that opens the
 * DeadlinePicker (Tasks, ReviewQueue, DeadlineAlertCenter).
 *
 * The account setting (`calendarInvites`: ask | always | never) lives in
 * /settings/user; we fetch it once per page load and cache it so the
 * picker never blocks on a network call. Settings.jsx calls
 * `setCalendarInviteMode` after saving so the cache stays in sync.
 *
 * The checkbox's last choice (ask mode only) is remembered per browser.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

const CHECKBOX_KEY = "sw_calendar_invite_checkbox_v1";
let cachedMode = null;          // "ask" | "always" | "never"
let inflight = null;
const listeners = new Set();

export function getCalendarInviteMode() {
  return cachedMode || "ask";
}

export function setCalendarInviteMode(mode) {
  cachedMode = ["ask", "always", "never"].includes(mode) ? mode : "ask";
  listeners.forEach((fn) => fn(cachedMode));
}

export async function loadCalendarInviteMode(force = false) {
  if (cachedMode && !force) return cachedMode;
  if (!inflight) {
    inflight = api.get("/settings/user")
      .then((r) => { setCalendarInviteMode(r.data?.calendarInvites); return cachedMode; })
      .catch(() => { if (!cachedMode) setCalendarInviteMode("ask"); return cachedMode; })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useCalendarInviteMode() {
  const [mode, setMode] = useState(getCalendarInviteMode());
  useEffect(() => {
    listeners.add(setMode);
    loadCalendarInviteMode();
    return () => listeners.delete(setMode);
  }, []);
  return mode;
}

export function getRememberedInviteChoice() {
  try {
    const v = localStorage.getItem(CHECKBOX_KEY);
    return v === null ? true : v === "1";
  } catch { return true; }
}

export function rememberInviteChoice(checked) {
  try { localStorage.setItem(CHECKBOX_KEY, checked ? "1" : "0"); } catch {}
}

/** True when a REQUEST invite was delivered for the task's *current* deadline. */
export function inviteIsCurrent(task) {
  const ci = task?.calendar_invite;
  if (!ci || ci.method !== "REQUEST" || !ci.sent_at || ci.last_error || !task?.deadline) return false;
  const a = new Date(ci.deadline).getTime();
  const b = new Date(task.deadline).getTime();
  return !isNaN(a) && a === b;
}
